import React, { createContext, useState, useCallback, useContext, ReactNode, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_MODEL_SELECTION, Message, ChatContextType, Conversation, ModelSelection } from '../types';
import { chatApi } from '../services/api';
import { notifyQueryComplete, notifyError } from '../utils/notificationUtils';
import { logger } from '../utils/Logger';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { useUser } from './UserContext';

// Create context with default values
const ChatContext = createContext<ChatContextType>({
  messages: [],
  isLoading: false,
  apiError: null,
  conversations: [],
  currentConversationId: null,
  currentStage: undefined,
  stageMessage: undefined,
  progress: undefined,
  pendingClarification: null,
  storageWarning: null,
  sendMessage: async () => {},
  stopMessage: () => {},
  clearMessages: () => {},
  enableMockMode: () => {},
  retryConnection: () => {},
  startNewConversation: () => null,
  loadConversation: () => {},
  deleteConversation: () => {},
  saveCurrentConversation: () => {},
  clearStorageWarning: () => {},
  getStorageInfo: () => ({}),
  forceCleanupOldConversations: () => ({ success: false, deletedCount: 0, keptCount: 0 }),
  exportConversationsAsJSON: () => {},
  importConversationsFromJSON: async () => ({ success: false, message: 'Not implemented' }),
  isSidebarCollapsed: false,
  setSidebarCollapsed: () => {},
  searchEnrichmentEnabled: false,
  setSearchEnrichmentEnabled: () => {},
  isConversationInProgress: () => false,
  updateMessageMetadata: () => {},
});

// Storage keys
const CONVERSATIONS_STORAGE_KEY = 'shipmentCocaptain_conversations';
const CURRENT_CONVERSATION_ID_KEY = 'shipmentCocaptain_currentConversationId';
const STORAGE_WARNING_SHOWN_KEY = 'shipmentCocaptain_storageWarningShown';
const NOTIFICATIONS_MUTED_KEY = 'shipmentCocaptain_notifications_muted';

// Storage limits
const MAX_CONVERSATIONS = 50;
const STORAGE_WARNING_THRESHOLD = 0.75; // Warn when 75% full
const CLEANUP_THRESHOLD = 0.9; // Prompt cleanup when 90% full
const MAX_CONVERSATIONS_PAYLOAD_BYTES = 3 * 1024 * 1024; // 3MB soft cap for conversation blob
const ESTIMATED_LOCALSTORAGE_LIMIT = 5 * 1024 * 1024; // 5MB conservative estimate
const MAX_TABLE_ROWS_STORED = 100;
const MAX_VISUALIZATION_JSON_BYTES = 250 * 1024;

const STALE_LOADING_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const STALE_CLEANUP_INTERVAL_MS = 60 * 1000; // sweep every 60s

const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] =>
  [...conversations].sort((a, b) => {
    const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    return timeB - timeA;
  });

const sanitizeMessageForStorage = (msg: Message): Message => {
  if (!msg.metadata) return msg;

  const metadata: Record<string, unknown> = { ...msg.metadata };

  // Dev-only debug payloads are not needed in persisted history
  delete metadata.searchEnrichmentDebug;

  if (metadata.tableData && Array.isArray(metadata.tableData)) {
    const rows = metadata.tableData as unknown[];
    if (rows.length > MAX_TABLE_ROWS_STORED) {
      metadata.tableData = rows.slice(0, MAX_TABLE_ROWS_STORED);
      metadata.tableDataTruncated = true;
    }
  }

  if (metadata.visualization) {
    try {
      const vizJson = JSON.stringify(metadata.visualization);
      if (vizJson.length > MAX_VISUALIZATION_JSON_BYTES) {
        const viz = metadata.visualization as Record<string, unknown>;
        metadata.visualization = {
          ...viz,
          data: undefined,
          visualizationTruncated: true,
        };
      }
    } catch {
      delete metadata.visualization;
    }
  }

  return { ...msg, metadata: metadata as Message['metadata'] };
};

const sanitizeConversationForStorage = (conv: Conversation): Conversation => ({
  ...conv,
  messages: conv.messages.map(sanitizeMessageForStorage),
});

const serializeConversationsForStorage = (conversations: Conversation[]): string =>
  JSON.stringify(conversations.map(sanitizeConversationForStorage));

const getConversationsPayloadBytes = (conversations: Conversation[]): number =>
  serializeConversationsForStorage(conversations).length;

const cleanupThinkingStatesForConversations = (conversationIds: string[]) => {
  conversationIds.forEach((id) => {
    try {
      localStorage.removeItem(`thinking_states_${id}`);
    } catch {
      // ignore
    }
  });
};

const cleanupOrphanThinkingStates = (activeConversationIds: string[]) => {
  const activeIds = new Set(activeConversationIds);
  try {
    for (const key in localStorage) {
      if (key.startsWith('thinking_states_')) {
        const convId = key.replace('thinking_states_', '');
        if (!activeIds.has(convId)) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch {
    // ignore
  }
};

const buildConversationExportPayload = (conversationsToExport: Conversation[]) => ({
  exportDate: new Date().toISOString(),
  totalConversations: conversationsToExport.length,
  conversations: conversationsToExport.map((conv) => ({
    id: conv.id,
    title: conv.title,
    timestamp: conv.timestamp instanceof Date ? conv.timestamp.toISOString() : conv.timestamp,
    lastMessagePreview: conv.lastMessagePreview,
    messageCount: conv.messages.length,
    messages: conv.messages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
      metadata: msg.metadata,
      followUps: msg.followUps,
      isFollowUp: msg.isFollowUp,
      contextMessageId: msg.contextMessageId,
    })),
  })),
});

/** User-initiated download only (export sidebar or manual archive cleanup). */
const triggerConversationFileDownload = (
  conversationsToExport: Conversation[],
  filenamePrefix = 'shipment-conversations'
): boolean => {
  if (!conversationsToExport.length) return false;

  try {
    const dataBlob = new Blob([JSON.stringify(buildConversationExportPayload(conversationsToExport), null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    link.download = `${filenamePrefix}-${dateStr}-${timeStr}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Failed to download conversations JSON:', error);
    return false;
  }
};

const checkStorageCapacity = () => {
  try {
    const testKey = '__storage_test__';
    const testData = 'x'.repeat(1024);

    localStorage.setItem(testKey, testData);
    localStorage.removeItem(testKey);

    let used = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += (localStorage[key]?.length || 0) + key.length;
      }
    }

    const usageRatio = used / ESTIMATED_LOCALSTORAGE_LIMIT;

    return {
      canStore: true,
      usageRatio,
      isNearFull: usageRatio > STORAGE_WARNING_THRESHOLD,
      requiresCleanup: usageRatio > CLEANUP_THRESHOLD,
      usedBytes: used,
      estimatedLimitBytes: ESTIMATED_LOCALSTORAGE_LIMIT,
    };
  } catch (error) {
    return {
      canStore: false,
      usageRatio: 1,
      isNearFull: true,
      requiresCleanup: true,
      usedBytes: 0,
      estimatedLimitBytes: 0,
      error: error instanceof Error ? error.message : 'Storage unavailable',
    };
  }
};

const isConversationHistoryHeavy = (conversations: Conversation[]): boolean => {
  const capacity = checkStorageCapacity();
  const payloadBytes = getConversationsPayloadBytes(conversations);
  return (
    conversations.length > MAX_CONVERSATIONS ||
    payloadBytes > MAX_CONVERSATIONS_PAYLOAD_BYTES ||
    capacity.isNearFull
  );
};

const getStorageWarningMessage = (conversations: Conversation[]): string => {
  const capacity = checkStorageCapacity();
  if (!capacity.canStore) {
    return 'Storage is full. Clear up conversations and archive locally, or delete chats manually.';
  }
  if (capacity.requiresCleanup || conversations.length > MAX_CONVERSATIONS) {
    return 'Conversation history is getting too long and may lead to slowdowns. Clear up conversations and archive locally, or delete old chats.';
  }
  return 'Conversation history is getting long and may lead to slowdowns. Consider clearing up conversations and archiving locally.';
};

/** Split into kept (newest) and removed (oldest) without downloading. */
const splitConversationsForCleanup = (
  conversationsList: Conversation[],
  maxKeep = MAX_CONVERSATIONS
): { kept: Conversation[]; removed: Conversation[] } => {
  const sorted = sortConversationsByTimestamp(conversationsList);
  const kept = sorted.slice(0, maxKeep);
  const removed = sorted.slice(maxKeep);

  let payloadBytes = getConversationsPayloadBytes(kept);
  while (kept.length > 1 && payloadBytes > MAX_CONVERSATIONS_PAYLOAD_BYTES) {
    const older = kept.pop();
    if (older) {
      removed.push(older);
    }
    payloadBytes = getConversationsPayloadBytes(kept);
  }

  return { kept, removed };
};

const countConversationsThatCanBeImported = (
  existing: Conversation[],
  candidates: Conversation[]
): { loadable: Conversation[]; skippedCount: number } => {
  const existingIds = new Set(existing.map((c) => c.id));
  const sortedCandidates = sortConversationsByTimestamp(
    candidates.filter((c) => !existingIds.has(c.id))
  );

  const loadable: Conversation[] = [];
  for (const candidate of sortedCandidates) {
    const trial = sortConversationsByTimestamp([...loadable, candidate, ...existing]);
    const withinCount = trial.length <= MAX_CONVERSATIONS;
    const withinPayload = getConversationsPayloadBytes(trial) <= MAX_CONVERSATIONS_PAYLOAD_BYTES;
    const capacity = checkStorageCapacity();

    if (withinCount && withinPayload && capacity.canStore) {
      loadable.push(candidate);
    } else {
      break;
    }
  }

  return {
    loadable,
    skippedCount: sortedCandidates.length - loadable.length,
  };
};

// Safe localStorage operations with error handling
const safeLocalStorageOps = {
  setItem: (key: string, value: string): { success: boolean; error?: string } => {
    try {
      localStorage.setItem(key, value);
      return { success: true };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      logger.error(`Failed to save to localStorage (${key})`, error, { key, operation: 'localStorage_set' });
      return { success: false, error: errorMsg };
    }
  },
  
  getItem: (key: string): { success: boolean; data?: string; error?: string } => {
    try {
      const data = localStorage.getItem(key);
      return { success: true, data: data || undefined };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      logger.error(`Failed to read from localStorage (${key})`, error, { key, operation: 'localStorage_get' });
      return { success: false, error: errorMsg };
    }
  },
  
  removeItem: (key: string): { success: boolean; error?: string } => {
    try {
      localStorage.removeItem(key);
      return { success: true };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      logger.error(`Failed to remove from localStorage (${key})`, error, { key, operation: 'localStorage_remove' });
      return { success: false, error: errorMsg };
    }
  }
};

// Helper to generate conversation title from messages
const generateConversationTitle = (messages: Message[]): string => {
  const userMessages = messages.filter(msg => msg.role === 'user');
  if (userMessages.length > 0) {
    // Use the first user message as the title, truncated to ~30 characters
    const firstMessage = userMessages[0].content;
    return firstMessage.length > 30 
      ? `${firstMessage.substring(0, 30)}...` 
      : firstMessage;
  }
  return 'New conversation';
};

// Helper to get the first few words of the last message
const getMessagePreview = (message: Message): string => {
  if (!message.content) return '';
  const text = typeof message.content === 'string' 
    ? message.content 
    : JSON.stringify(message.content);
  
  const preview = text.replace(/[#*_~`]/g, ''); // Remove Markdown formatting
  return preview.length > 40 ? `${preview.substring(0, 40)}...` : preview;
};

// Chat provider component
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userEmail, sessionId } = useUser();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [apiError, setApiError] = useState<Error | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  // Ref mirrors currentConversationId for async callbacks (avoids stale closure after startNewConversation)
  const currentConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);
  
  // Track initialization state to prevent race conditions during localStorage load
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Conversation-specific loading states - stored per conversation ID
  const [conversationLoadingStates, setConversationLoadingStates] = useState<Map<string, {
    isLoading: boolean;
    startedAt: number;
    currentStage?: string;
    stageMessage?: string;
    progress?: number;
    jobId?: string;
    abortController?: AbortController;
  }>>(new Map());
  
  // Computed states for the current conversation (to maintain interface compatibility)
  const currentLoadingState = currentConversationId ? conversationLoadingStates.get(currentConversationId) : undefined;
  const currentStage = currentLoadingState?.currentStage;
  const stageMessage = currentLoadingState?.stageMessage;
  const progress = currentLoadingState?.progress;
  
  // Override isLoading to be conversation-specific
  const conversationIsLoading = currentLoadingState?.isLoading ?? false;
  
  // Helper functions to manage conversation-specific loading states
  const updateConversationLoadingState = useCallback((conversationId: string, updates: Partial<{
    isLoading: boolean;
    startedAt: number;
    currentStage?: string;
    stageMessage?: string;
    progress?: number;
    jobId?: string;
    abortController?: AbortController;
  }>) => {
    setConversationLoadingStates(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(conversationId) || {
        isLoading: false,
        startedAt: Date.now(),
        currentStage: undefined,
        stageMessage: undefined,
        progress: undefined,
        jobId: undefined,
        abortController: undefined,
      };
      const isProgressUpdate = updates.currentStage !== undefined
        || updates.stageMessage !== undefined
        || updates.progress !== undefined;
      newMap.set(conversationId, {
        ...existing,
        ...updates,
        startedAt: updates.startedAt ?? (isProgressUpdate ? Date.now() : existing.startedAt),
      });
      return newMap;
    });
  }, []);
  
  const clearConversationLoadingState = useCallback((conversationId: string) => {
    setConversationLoadingStates(prev => {
      const newMap = new Map(prev);
      const state = newMap.get(conversationId);
      if (state?.abortController) {
        state.abortController.abort();
      }
      newMap.delete(conversationId);
      return newMap;
    });
  }, []);
  
  const getConversationLoadingState = useCallback((conversationId: string) => {
    return conversationLoadingStates.get(conversationId);
  }, [conversationLoadingStates]);

  const isConversationInProgress = useCallback((conversationId: string): boolean => {
    const state = conversationLoadingStates.get(conversationId);
    if (!state?.isLoading) return false;
    if (Date.now() - state.startedAt > STALE_LOADING_TIMEOUT_MS) {
      clearConversationLoadingState(conversationId);
      return false;
    }
    return true;
  }, [conversationLoadingStates, clearConversationLoadingState]);

  // Clean up loading states when switching conversations or starting new ones
  useEffect(() => {
    // This effect ensures we don't leak loading states across conversations
    // When currentConversationId changes, we maintain only the current conversation's state
  }, [currentConversationId]);

  // Periodically sweep stale loading entries that exceed the TTL
  useEffect(() => {
    const interval = setInterval(() => {
      setConversationLoadingStates(prev => {
        const now = Date.now();
        let changed = false;
        const newMap = new Map(prev);
        newMap.forEach((state, id) => {
          if (state.isLoading && now - state.startedAt > STALE_LOADING_TIMEOUT_MS) {
            if (state.abortController) state.abortController.abort();
            newMap.delete(id);
            changed = true;
          }
        });
        return changed ? newMap : prev;
      });
    }, STALE_CLEANUP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);
  
  // Clarification state
  const [pendingClarification, setPendingClarification] = useState<{
    originalQuery: string;
    questions: string[];
  } | null>(null);
  
  // Storage management state
  const [storageWarning, setStorageWarning] = useState<{
    show: boolean;
    message: string;
    canStore: boolean;
  } | null>(null);

  // UI state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Dev feature: Search enrichment toggle (OFF by default)
  const [searchEnrichmentEnabled, setSearchEnrichmentEnabled] = useState(false);

  // Helper to update sidebar collapsed state
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  // Load conversations from localStorage with robust error handling
  useEffect(() => {
    const loadConversations = () => {
      // First, check storage capacity
      const capacity = checkStorageCapacity();
      
      // Load conversations
      const conversationsResult = safeLocalStorageOps.getItem(CONVERSATIONS_STORAGE_KEY);
      if (conversationsResult.success && conversationsResult.data) {
        try {
          const savedConversations = JSON.parse(conversationsResult.data);
          
            // Validate conversations structure
            if (Array.isArray(savedConversations)) {
            const parsedConversations = savedConversations
              .filter((conv: any) => conv && conv.id && conv.messages && Array.isArray(conv.messages))
              .map((conv: any) => ({
                ...conv,
                timestamp: conv.timestamp ? new Date(conv.timestamp) : new Date(),
                messages: conv.messages
                  .filter((msg: any) => msg && msg.id && msg.role && msg.content)
                  .map((msg: any) => ({
                    ...msg,
                    timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
                  }))
              }))
              .filter((conv: any) => conv.messages.length > 0);

            cleanupOrphanThinkingStates(parsedConversations.map((c: Conversation) => c.id));

            setConversations(parsedConversations);

            if (isConversationHistoryHeavy(parsedConversations)) {
              setStorageWarning({
                show: true,
                message: getStorageWarningMessage(parsedConversations),
                canStore: capacity.canStore,
              });
            }
          } else {
            console.warn('Invalid conversations format in localStorage, starting fresh');
            setConversations([]);
          }
        } catch (parseError) {
          console.error('Error parsing conversations from localStorage:', parseError);
          setConversations([]);
          // Clear corrupted data
          safeLocalStorageOps.removeItem(CONVERSATIONS_STORAGE_KEY);
        }
      } else if (conversationsResult.error) {
        console.error('Failed to load conversations:', conversationsResult.error);
        setConversations([]);
      }

      // Load current conversation ID
      const currentIdResult = safeLocalStorageOps.getItem(CURRENT_CONVERSATION_ID_KEY);
      if (currentIdResult.success && currentIdResult.data) {
        const savedCurrentId = currentIdResult.data;
        setCurrentConversationId(savedCurrentId);
        
        // Load messages from the current conversation
        if (conversationsResult.success && conversationsResult.data) {
          try {
            const savedConversations = JSON.parse(conversationsResult.data);
            const currentConv = savedConversations.find((c: any) => c && c.id === savedCurrentId);
            if (currentConv && currentConv.messages && Array.isArray(currentConv.messages)) {
              // Validate and convert messages
              const validMessages = currentConv.messages
                .filter((msg: any) => msg && msg.id && msg.role && msg.content)
                .map((msg: any) => ({
                  ...msg,
                  timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
                }));
              setMessages(validMessages);
            } else {
              console.warn('Current conversation not found or invalid, starting fresh');
              setMessages([]);
              // Clear invalid current conversation ID
              safeLocalStorageOps.removeItem(CURRENT_CONVERSATION_ID_KEY);
              setCurrentConversationId(null);
            }
          } catch (parseError) {
            console.error('Error parsing current conversation:', parseError);
            setMessages([]);
            setCurrentConversationId(null);
            safeLocalStorageOps.removeItem(CURRENT_CONVERSATION_ID_KEY);
          }
        }
      }
    };
    
    loadConversations();
    
    // Mark as initialized after localStorage loading is complete
    // This runs synchronously, so by the time this line executes, all state has been set
    setIsInitialized(true);
    console.log('[INIT] ChatContext initialized, localStorage loaded');
  }, []);

  // Save conversations with capacity checking and error handling
  const saveConversations = useCallback((convsToSave: Conversation[]) => {
    try {
      // Check storage capacity before saving
      const capacity = checkStorageCapacity();
      
      if (!capacity.canStore) {
        console.error('Cannot save conversations: Storage is full');
        setStorageWarning({
          show: true,
          message: 'Storage is full. Clear up conversations and archive locally, or delete old chats manually.',
          canStore: false
        });
        return false;
      }

      const validConversations = convsToSave.filter(conv => 
        conv && conv.id && conv.messages && Array.isArray(conv.messages) && conv.messages.length > 0
      );

      const serialized = serializeConversationsForStorage(validConversations);
      const result = safeLocalStorageOps.setItem(CONVERSATIONS_STORAGE_KEY, serialized);

      if (!result.success) {
        console.error('Failed to save conversations:', result.error);
        setStorageWarning({
          show: true,
          message: 'Failed to save conversations. Your chats may not persist across browser sessions.',
          canStore: false
        });
        return false;
      }

      if (isConversationHistoryHeavy(validConversations)) {
        setStorageWarning({
          show: true,
          message: getStorageWarningMessage(validConversations),
          canStore: true,
        });
      }

      return true;
    } catch (error) {
      console.error('Error saving conversations to localStorage:', error);
      setStorageWarning({
        show: true,
        message: 'Failed to save conversations due to an error.',
        canStore: false
      });
      return false;
    }
  }, []);

  // Start a new conversation with storage capacity check
  const startNewConversation = useCallback((): string | null => {
    // Check storage capacity before creating new conversation
    const capacity = checkStorageCapacity();
    
    if (!capacity.canStore) {
      setStorageWarning({
        show: true,
        message: 'Storage is full! Cannot create new conversations. Please clear browser data or delete old conversations.',
        canStore: false
      });
      return null;
    }
    
    if (capacity.isNearFull) {
      const warningShownResult = safeLocalStorageOps.getItem(STORAGE_WARNING_SHOWN_KEY);
      const warningShown = warningShownResult.success && warningShownResult.data === 'true';
      
      if (!warningShown) {
        setStorageWarning({
          show: true,
          message: getStorageWarningMessage(conversations),
          canStore: true
        });
        
        // Mark warning as shown
        safeLocalStorageOps.setItem(STORAGE_WARNING_SHOWN_KEY, 'true');
      }
    }
    
    // Generate timestamp-based conversation ID (BIGINT compatible)
    // Use milliseconds since epoch to ensure uniqueness and database compatibility
    const newConversationId = Date.now().toString();
    setCurrentConversationId(newConversationId);
    currentConversationIdRef.current = newConversationId;
    
    // Log conversation start
    if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
      logger.logConversationStart(userEmail, newConversationId, sessionId);
    }
    
    const result = safeLocalStorageOps.setItem(CURRENT_CONVERSATION_ID_KEY, newConversationId);
    if (!result.success) {
      console.error('Failed to save current conversation ID:', result.error);
    }
    
    setMessages([]);
    setApiError(null);
    setPendingClarification(null); // Clear any pending clarifications
    
    // Clear any existing storage warnings on successful new conversation
    if (capacity.canStore) {
      setStorageWarning(null);
    }
    
    return newConversationId;
  }, [userEmail, sessionId, conversations]);

  // Save the current conversation
  // IMPORTANT: This function now accepts an optional conversationId parameter to avoid
  // race conditions with React's async state updates. When calling this immediately after
  // startNewConversation(), the React state (currentConversationId) may not have updated yet,
  // so we pass the ID explicitly to ensure the conversation is saved correctly.
  const saveCurrentConversation = useCallback((messagesToSave?: Message[], conversationIdOverride?: string) => {
    const currentMessages = messagesToSave || messages;
    if (currentMessages.length === 0) {
      console.log('[SAVE_CONV] Skipping save - no messages to save');
      return; // Don't save empty conversations
    }
    
    // Use the explicitly passed ID, or fall back to state (for backwards compatibility)
    const effectiveConversationId = conversationIdOverride || currentConversationId;
    
    if (!effectiveConversationId) {
      console.error('[SAVE_CONV] CRITICAL: No conversation ID available! This should never happen.');
      console.error('[SAVE_CONV] State currentConversationId:', currentConversationId);
      console.error('[SAVE_CONV] Override conversationId:', conversationIdOverride);
      console.error('[SAVE_CONV] Messages to save:', currentMessages.length);
      // Attempt recovery by generating an emergency ID
      const emergencyId = Date.now().toString();
      console.warn('[SAVE_CONV] Generating emergency conversation ID:', emergencyId);
      // Continue with emergency ID rather than losing the conversation
      const title = generateConversationTitle(currentMessages);
      const lastMessage = currentMessages[currentMessages.length - 1];
      const emergencyConversation: Conversation = {
        id: emergencyId,
        title,
        timestamp: new Date(),
        messages: currentMessages,
        lastMessagePreview: getMessagePreview(lastMessage)
      };
      const updatedConversations = [emergencyConversation, ...conversations];
      setConversations(updatedConversations);
      saveConversations(updatedConversations);
      // Also update the current conversation ID state
      setCurrentConversationId(emergencyId);
      safeLocalStorageOps.setItem(CURRENT_CONVERSATION_ID_KEY, emergencyId);
      return;
    }
    
    console.log('[SAVE_CONV] Saving conversation', effectiveConversationId, 'with', currentMessages.length, 'messages');
    
    const conversationExists = conversations.some(c => c.id === effectiveConversationId);
    const title = generateConversationTitle(currentMessages);
    const lastMessage = currentMessages[currentMessages.length - 1];
    
    if (conversationExists) {
      // Update existing conversation
      console.log('[SAVE_CONV] Updating existing conversation:', effectiveConversationId);
      const updatedConversations = conversations.map(conv => 
        conv.id === effectiveConversationId 
          ? { 
              ...conv, 
              title, 
              messages: currentMessages, 
              timestamp: new Date(),
              lastMessagePreview: getMessagePreview(lastMessage)
            }
          : conv
      );
      setConversations(updatedConversations);
      saveConversations(updatedConversations);
    } else {
      // Create new conversation - no need to check if ID exists, we already verified above
      console.log('[SAVE_CONV] Creating new conversation:', effectiveConversationId);
      const newConversation: Conversation = {
        id: effectiveConversationId,
        title,
        timestamp: new Date(),
        messages: currentMessages,
        lastMessagePreview: getMessagePreview(lastMessage)
      };
      
      const updatedConversations = [newConversation, ...conversations];
      setConversations(updatedConversations);
      saveConversations(updatedConversations);
    }
  }, [conversations, currentConversationId, messages, saveConversations]);

  const updateMessageMetadata = useCallback((messageId: string, metadataUpdate: Record<string, any>) => {
    setMessages(prev => {
      const updated = prev.map(msg =>
        msg.id === messageId
          ? { ...msg, metadata: { ...msg.metadata, ...metadataUpdate } }
          : msg
      );
      saveCurrentConversation(updated);
      return updated;
    });
  }, [saveCurrentConversation]);

  // Load a specific conversation
  const loadConversation = useCallback((conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      console.log('Loading conversation with', conversation.messages.length, 'messages');
      setMessages(conversation.messages);
      setCurrentConversationId(conversationId);
      localStorage.setItem(CURRENT_CONVERSATION_ID_KEY, conversationId);
    } else {
      console.warn('Conversation not found:', conversationId);
    }
  }, [conversations]);

  // Delete a specific conversation
  const deleteConversation = useCallback((conversationId: string) => {
    cleanupThinkingStatesForConversations([conversationId]);

    const updatedConversations = conversations.filter(c => c.id !== conversationId);
    setConversations(updatedConversations);
    saveConversations(updatedConversations);
    
    // If we're deleting the current conversation, start a new one
    if (currentConversationId === conversationId) {
      startNewConversation();
    }
  }, [conversations, currentConversationId, saveConversations, startNewConversation]);

  // Storage management functions
  const clearStorageWarning = useCallback(() => {
    setStorageWarning(null);
  }, []);
  
  const getStorageInfo = useCallback(() => {
    return checkStorageCapacity();
  }, []);
  
  const forceCleanupOldConversations = useCallback(() => {
    const { kept, removed } = splitConversationsForCleanup(conversations, MAX_CONVERSATIONS);
    const deletedCount = removed.length;

    if (deletedCount === 0) {
      setStorageWarning({
        show: true,
        message: 'Conversation history is within limits. No chats were removed.',
        canStore: true,
      });
      setTimeout(() => setStorageWarning(null), 5000);
      return { success: true, deletedCount: 0, keptCount: kept.length };
    }

    const downloaded = triggerConversationFileDownload(removed, 'shipment-conversations-archive');
    if (!downloaded) {
      setStorageWarning({
        show: true,
        message: 'Failed to download archive file. Conversations were not removed.',
        canStore: true,
      });
      return { success: false, deletedCount: 0, keptCount: conversations.length };
    }

    cleanupThinkingStatesForConversations(removed.map((c) => c.id));
    setConversations(kept);
    const success = saveConversations(kept);

    if (success) {
      if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING) {
        logger.info('Conversation manual archive cleanup', {
          keptConversations: kept.length,
          archivedConversations: deletedCount,
          action: 'manual_archive_cleanup',
        });
      }

      setStorageWarning({
        show: true,
        message: `Archived ${deletedCount} older conversations locally and removed them from browser storage. Kept ${kept.length} recent conversations.`,
        canStore: true,
      });

      setTimeout(() => {
        setStorageWarning(null);
      }, 6000);
    }

    return { success, deletedCount, keptCount: kept.length };
  }, [conversations, saveConversations]);

  // Export conversations as JSON file
  const exportConversationsAsJSON = useCallback(() => {
    const success = triggerConversationFileDownload(conversations, 'shipment-conversations');
    if (success) {
      setStorageWarning({
        show: true,
        message: `Successfully exported ${conversations.length} conversations to JSON file.`,
        canStore: true
      });
      setTimeout(() => {
        setStorageWarning(null);
      }, 3000);
    } else {
      setStorageWarning({
        show: true,
        message: 'Failed to export conversations. Please try again.',
        canStore: false
      });
      setTimeout(() => {
        setStorageWarning(null);
      }, 5000);
    }
  }, [conversations]);

  // Import conversations from JSON file
  const importConversationsFromJSON = useCallback(async (file: File): Promise<{ success: boolean; message: string; imported?: number }> => {
    try {
      // Check file type
      if (!file.name.toLowerCase().endsWith('.json')) {
        return { success: false, message: 'Please select a JSON file.' };
      }

      // Check file size (limit to 15MB)
      if (file.size > 15 * 1024 * 1024) {
        return { success: false, message: 'File is too large. Maximum size is 15MB.' };
      }

      // Read file content
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });

      // Parse JSON
      let importData;
      try {
        importData = JSON.parse(fileContent);
      } catch (parseError) {
        return { success: false, message: 'Invalid JSON file format.' };
      }

      // Validate import data structure
      if (!importData.conversations || !Array.isArray(importData.conversations)) {
        return { success: false, message: 'Invalid conversation data format.' };
      }

      // Process and validate conversations
      const validConversations: Conversation[] = [];
      for (const conv of importData.conversations) {
        if (!conv.id || !conv.title || !conv.messages || !Array.isArray(conv.messages)) {
          continue; // Skip invalid conversations
        }

        // Validate and process messages
        const validMessages: Message[] = [];
        for (const msg of conv.messages) {
          if (!msg.id || !msg.role || !msg.content) {
            continue; // Skip invalid messages
          }

          validMessages.push({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            metadata: msg.metadata || {},
            followUps: msg.followUps,
            isFollowUp: msg.isFollowUp,
            contextMessageId: msg.contextMessageId
          });
        }

        if (validMessages.length > 0) {
          validConversations.push({
            id: conv.id,
            title: conv.title,
            timestamp: conv.timestamp ? new Date(conv.timestamp) : new Date(),
            messages: validMessages,
            lastMessagePreview: conv.lastMessagePreview || ''
          });
        }
      }

      if (validConversations.length === 0) {
        return { success: false, message: 'No valid conversations found in the file.' };
      }

      const totalInFile = validConversations.length;
      const duplicateCount = validConversations.filter((c) => conversations.some((e) => e.id === c.id)).length;
      const { loadable, skippedCount } = countConversationsThatCanBeImported(conversations, validConversations);

      if (loadable.length === 0) {
        if (duplicateCount === totalInFile) {
          return { success: false, message: 'All conversations in the file already exist.' };
        }
        return {
          success: false,
          message: 'No conversations could be loaded without exceeding storage limits. Clear up conversations and archive locally first.',
        };
      }

      const finalConversations = sortConversationsByTimestamp([...loadable, ...conversations]);

      setConversations(finalConversations);
      const saveSuccess = saveConversations(finalConversations);

      if (!saveSuccess) {
        return { success: false, message: 'Failed to save imported conversations to storage.' };
      }

      const skippedDuplicates = duplicateCount;
      const skippedDueToLimits = skippedCount;
      let message = `Loaded ${loadable.length} of ${totalInFile} conversations from file.`;
      if (skippedDuplicates > 0) {
        message += ` ${skippedDuplicates} already existed and were merged without duplication.`;
      }
      if (skippedDueToLimits > 0) {
        message += ` ${skippedDueToLimits} could not be loaded due to storage limits.`;
      }

      setStorageWarning({
        show: true,
        message,
        canStore: true
      });

      setTimeout(() => {
        setStorageWarning(null);
      }, 5000);

      return { success: true, message, imported: loadable.length };

    } catch (error) {
      console.error('Error importing conversations:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, message: `Failed to import conversations: ${errorMessage}` };
    }
  }, [conversations, saveConversations]);

  const stopMessage = useCallback(() => {
    console.log('Stop button clicked - interrupting all processing');
    
    if (!currentConversationId) return;
    
    const loadingState = getConversationLoadingState(currentConversationId);
    
    // First, abort any ongoing API polling
    if (loadingState?.abortController) {
      console.log('Aborting ongoing API requests');
      loadingState.abortController.abort();
    }
    
    // Then try to cancel the job on the backend
    if (loadingState?.jobId) {
      console.log(`Stopping job: ${loadingState.jobId}`);
      // Cancel the current job (fire and forget - don't wait for response)
      chatApi.cancelJob(loadingState.jobId).catch((error: any) => {
        console.warn('Failed to cancel job:', error);
      });
    }
    
    // Reset conversation-specific states immediately (don't wait for cancellation to complete)
    clearConversationLoadingState(currentConversationId);
    
    // Clear any pending citation statuses to prevent stuck "Adding citations..." indicators
    const messagesWithClearedCitations = messages.map(message => {
      if (message.metadata?.citationStatus === 'pending') {
        return {
          ...message,
          metadata: {
            ...message.metadata,
            citationStatus: 'completed' as const, // Mark as completed to clear the loading indicator
          }
        };
      }
      return message;
    });
    
    // Add a system message indicating the task was stopped
    const stopMessageObj: Message = {
      id: uuidv4(),
      role: 'system',
      content: 'Task stopped by user.',
      timestamp: new Date(),
    };
    
    const updatedMessages = [...messagesWithClearedCitations, stopMessageObj];
    setMessages(updatedMessages);
    // Pass currentConversationId explicitly to handle race conditions
    saveCurrentConversation(updatedMessages, currentConversationId || undefined);
  }, [currentConversationId, getConversationLoadingState, clearConversationLoadingState, messages, saveCurrentConversation]);

  // Send message with polling support and real-time stage updates
  const sendMessage = useCallback(async (content: string, options?: {
    isFollowUp?: boolean;
    contextMessageId?: string;
    model?: ModelSelection;
  }) => {
    try {
      const selectedModel = options?.model || DEFAULT_MODEL_SELECTION;
      // Log initialization state for debugging
      console.log('[SEND_MSG] Starting message send, isInitialized:', isInitialized, 'currentConversationId:', currentConversationId);
      
      // Ensure we have a conversation ID
      let activeConversationId = currentConversationId;
      if (!activeConversationId) {
        console.log('[SEND_MSG] No conversation ID, creating new conversation');
        activeConversationId = startNewConversation();
        console.log('[SEND_MSG] New conversation ID created:', activeConversationId);
        if (!activeConversationId) {
          // Failed to create new conversation (storage full)
          console.error('[SEND_MSG] Failed to create new conversation (storage full or error)');
          return;
        }
      }
      
      // Set conversation-specific loading state
      updateConversationLoadingState(activeConversationId, {
        isLoading: true,
        startedAt: Date.now(),
        currentStage: 'pending',
        stageMessage: 'Starting...',
        progress: 0,
      });
      
      // Enhanced polling verification: check if we're receiving progress updates
      // If not, attempt to re-establish polling connection
      let progressUpdateReceived = false;
      const pollingVerificationTimeout = setTimeout(async () => {
        if (activeConversationId && !progressUpdateReceived) {
          console.warn('[POLLING_VERIFICATION] No progress updates received after 5 seconds, checking job status directly');
          
          // Try to get job status directly to verify backend is responding
          const currentState = getConversationLoadingState(activeConversationId);
          if (currentState?.jobId) {
            try {
              // Direct status check to verify if backend has progressed beyond pending
              const directStatus = await chatApi.getJobStatus(currentState.jobId);
              console.log('[POLLING_VERIFICATION] Direct status check result:', directStatus);
              
              // If backend shows progress but frontend hasn't updated, force sync
              if (directStatus.current_stage && directStatus.current_stage !== 'pending') {
                console.warn('[POLLING_VERIFICATION] Backend has progressed, syncing frontend state');
                updateConversationLoadingState(activeConversationId, {
                  currentStage: directStatus.current_stage,
                  stageMessage: directStatus.stage_message || 'Processing your request...',
                  progress: directStatus.progress || 5,
                });
                progressUpdateReceived = true; // Mark as received to prevent further checks
              } else if (directStatus.current_stage === 'pending') {
                // Backend is still pending, show intermediate state
                updateConversationLoadingState(activeConversationId, {
                  currentStage: 'understanding',
                  stageMessage: 'Initializing request processing...',
                  progress: 2,
                });
              }
            } catch (error) {
              console.warn('[POLLING_VERIFICATION] Direct status check failed:', error);
              // Fallback: show that we're trying to process
              updateConversationLoadingState(activeConversationId, {
                currentStage: 'understanding',
                stageMessage: 'Connecting to processing service...',
                progress: 3,
              });
            }
          } else {
            // No job ID yet, backend might be slow to respond
            console.warn('[POLLING_VERIFICATION] No job ID available, backend may be overloaded');
            updateConversationLoadingState(activeConversationId, {
              currentStage: 'understanding',
              stageMessage: 'Getting started...',
              progress: 1,
            });
          }
        }
      }, 5000); // 5 second verification timeout
      
      // Function to mark progress as received and clear verification timeout
      const markProgressReceived = () => {
        progressUpdateReceived = true;
        clearTimeout(pollingVerificationTimeout);
      };
      
      setApiError(null);
      
      let previousUserMessage: string | undefined;
      let previousAssistantResponse: string | undefined;
      
      // Check if there are already messages in the conversation
      if (messages.length >= 2) {
        const lastIndex = messages.length - 1;
        
        for (let i = lastIndex; i >= 0; i--) {
          if (messages[i].role === 'assistant') {
            const assistantMessage = messages[i];
            
            if (i > 0 && messages[i - 1].role === 'user') {
              const userMessage = messages[i - 1];
              
              previousUserMessage = userMessage.content;
              previousAssistantResponse = typeof assistantMessage.content === 'string' 
                ? assistantMessage.content 
                : JSON.stringify(assistantMessage.content);
              
              break;
            }
          }
        }
      }
      
      // Still respect explicit follow-up options if provided
      if (options?.isFollowUp && options?.contextMessageId) {
        const contextIndex = messages.findIndex(msg => msg.id === options.contextMessageId);
        if (contextIndex >= 0 && contextIndex > 0) {
          // Get the specific message referenced by contextMessageId
          const assistantMessage = messages[contextIndex];
          const userMessage = messages[contextIndex - 1];
          
          if (assistantMessage.role === 'assistant' && userMessage.role === 'user') {
            previousUserMessage = userMessage.content;
            previousAssistantResponse = typeof assistantMessage.content === 'string' 
              ? assistantMessage.content 
              : JSON.stringify(assistantMessage.content);
          }
        }
      }
      
      // Add user message to state
      const userMessage: Message = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date(),
        isFollowUp: options?.isFollowUp,
        contextMessageId: options?.contextMessageId,
      };

      // Log user query
      if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
        logger.logUserQuery(userEmail, activeConversationId, userMessage.id, content, sessionId);
      }
      
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      
      // Save conversation immediately when user sends a message (before waiting for response)
      // This ensures the conversation appears in the sidebar right away
      // CRITICAL: Pass activeConversationId explicitly to avoid race condition with React state
      saveCurrentConversation(updatedMessages, activeConversationId);
      
      // Determine if this is a clarification response
      const isClarificationResponse = pendingClarification !== null;
      
      let jobId: string | null = null;
      
      // Create an AbortController for this request
      const abortController = new AbortController();
      updateConversationLoadingState(activeConversationId, { abortController });
      
      // Use the appropriate API method
      let initialResponseReceived = false;
      let responseMessage: Message | null = null;
      
      const response = isClarificationResponse
        ? await chatApi.provideClarification(
            { 
              message: content,
              conversationId: currentConversationId || undefined,
              previous_user_message: previousUserMessage,
              previous_assistant_response: previousAssistantResponse,
              original_query: pendingClarification?.originalQuery,
              is_clarification: true,
              user_email: userEmail || undefined,  // Include user email for logging
              enable_search_enrichment: searchEnrichmentEnabled,  // Dev toggle for search enrichment
              model_selection: selectedModel,
            },
            // Progress callback for real-time stage updates
            (status: any) => {
              // More robust condition check - use the original activeConversationId from closure
              if (activeConversationId) {
                console.log(`[PROGRESS-CLARIFY] Job ${status.job_id || 'unknown'} - Stage: ${status.current_stage}, Message: ${status.stage_message}, Progress: ${status.progress}`);
                
                // Mark that we received a progress update
                markProgressReceived();
                
                updateConversationLoadingState(activeConversationId, {
                  currentStage: status.current_stage,
                  stageMessage: status.stage_message,
                  progress: status.progress,
                });
                if (!jobId && status.job_id) {
                  jobId = status.job_id;
                  updateConversationLoadingState(activeConversationId, { jobId: status.job_id });
                  
                  // Force an immediate progress update to ensure UI responds
                  setTimeout(() => {
                    if (activeConversationId) {
                      updateConversationLoadingState(activeConversationId, {
                        currentStage: status.current_stage || 'understanding',
                        stageMessage: status.stage_message || 'Processing clarification...',
                        progress: Math.max(status.progress || 0, 5),
                      });
                    }
                  }, 100);
                }
              } else {
                console.warn('[PROGRESS-CLARIFY] activeConversationId is null, skipping progress update');
              }
            },
            abortController.signal
          )
        : await chatApi.sendMessageWithTwoPhaseResponse(
            { 
              message: content,
              conversationId: currentConversationId || undefined,
              previous_user_message: previousUserMessage,
              previous_assistant_response: previousAssistantResponse,
              user_email: userEmail || undefined,  // Include user email for logging
              enable_search_enrichment: searchEnrichmentEnabled,  // Dev toggle for search enrichment
              model_selection: selectedModel,
            },
            // Progress callback for real-time stage updates
            (status: any) => {
              // More robust condition check - use the original activeConversationId from closure
              // instead of current state to avoid race conditions
              if (activeConversationId) {
                console.log(`[PROGRESS] Job ${status.job_id || 'unknown'} - Stage: ${status.current_stage}, Message: ${status.stage_message}, Progress: ${status.progress}`);
                
                // Mark that we received a progress update
                markProgressReceived();
                
                updateConversationLoadingState(activeConversationId, {
                  currentStage: status.current_stage,
                  stageMessage: status.stage_message,
                  progress: status.progress,
                });
                if (!jobId && status.job_id) {
                  jobId = status.job_id;
                  updateConversationLoadingState(activeConversationId, { jobId: status.job_id });
                  
                  // Force an immediate progress update to ensure UI responds
                  setTimeout(() => {
                    if (activeConversationId) {
                      updateConversationLoadingState(activeConversationId, {
                        currentStage: status.current_stage || 'understanding',
                        stageMessage: status.stage_message || 'Processing your request...',
                        progress: Math.max(status.progress || 0, 5),
                      });
                    }
                  }, 100);
                }
              } else {
                console.warn('[PROGRESS] activeConversationId is null, skipping progress update');
              }
            },
            // onResponseReady callback - display response immediately when ready
            (responseReadyData: any) => {
              if (currentConversationIdRef.current === activeConversationId && activeConversationId && !initialResponseReceived) {
                console.log('Response ready callback triggered, displaying initial response');
                initialResponseReceived = true;
                
                // Create the assistant's response message with initial data
                responseMessage = {
                  id: uuidv4(),
                  role: 'assistant',
                  content: responseReadyData.cited_response || responseReadyData.response || "Unable to process your request.",
                  timestamp: new Date(),
                  metadata: {
                    citationStatus: 'pending',
                    jobId: jobId || undefined,
                    model: selectedModel,
                  },
                };
                
                // Add response metadata
                if (responseReadyData.sqlQuery) {
                  responseMessage.metadata!.sqlQuery = responseReadyData.sqlQuery;
                }
                if (responseReadyData.tableData) {
                  responseMessage.metadata!.tableData = responseReadyData.tableData;
                  responseMessage.metadata!.tableTitle = responseReadyData.metadata?.tableTitle;
                }
                if (responseReadyData.visualization) {
                  responseMessage.metadata!.visualization = responseReadyData.visualization;
                }
                if (responseReadyData.followUpSuggestions) {
                  responseMessage.followUps = responseReadyData.followUpSuggestions;
                }
                
                // Add initial citations if available
                if (responseReadyData.citation_descriptions) {
                  responseMessage.metadata!.citationDescriptions = responseReadyData.citation_descriptions;
                }
                if (responseReadyData.citation_metadata) {
                  responseMessage.metadata!.citationMetadata = responseReadyData.citation_metadata;
                }
                
                // Add search enrichment if available
                if (responseReadyData.search_enrichment) {
                  responseMessage.metadata!.searchEnrichment = responseReadyData.search_enrichment;
                }
                // Add search enrichment debug info if available (dev mode only)
                if (responseReadyData.search_enrichment_debug) {
                  responseMessage.metadata!.searchEnrichmentDebug = responseReadyData.search_enrichment_debug;
                }
                
                const finalMessages = [...updatedMessages, responseMessage];
                setMessages(finalMessages);
                // Pass activeConversationId explicitly for reliability
                saveCurrentConversation(finalMessages, activeConversationId);
                
                // Trigger notification for query completion (initial response ready) if not muted
                try {
                  const muted = localStorage.getItem(NOTIFICATIONS_MUTED_KEY) === 'true';
                  if (!muted) {
                    notifyQueryComplete(content, responseMessage.content).catch(err => 
                      console.warn('Failed to send notification:', err)
                    );
                  }
                } catch (e) {
                  // ignore localStorage errors
                }
                
                // Update loading state to show citation processing - don't clear it yet
                updateConversationLoadingState(activeConversationId, {
                  currentStage: 'citations',
                  stageMessage: 'Adding citations...',
                  progress: 95,
                });
              }
            },
            // onCitationsReady callback - update response when citations are complete (may be called twice: once for citations_ready, once for completed)
            (citationsReadyData: any) => {
              if (currentConversationIdRef.current === activeConversationId && activeConversationId && responseMessage) {
                console.log('Citations/enrichment ready callback triggered, updating response');
                
                // Update the response message with citation and/or enrichment data
                const updatedMessage = { ...responseMessage };
                
                // Update content if the final response has better citation integration
                if (citationsReadyData.cited_response && citationsReadyData.cited_response !== updatedMessage.content) {
                  updatedMessage.content = citationsReadyData.cited_response;
                }
                
                // Update citations with data (if available)
                if (citationsReadyData.citation_descriptions) {
                  updatedMessage.metadata!.citationDescriptions = citationsReadyData.citation_descriptions;
                  // Mark citations as completed when we have citation descriptions
                  updatedMessage.metadata!.citationStatus = 'completed';
                }
                if (citationsReadyData.citation_metadata) {
                  updatedMessage.metadata!.citationMetadata = citationsReadyData.citation_metadata;
                }
                
                // Update search enrichment with final data (if available)
                if (citationsReadyData.search_enrichment) {
                  updatedMessage.metadata!.searchEnrichment = citationsReadyData.search_enrichment;
                }
                // Update search enrichment debug info (dev mode only)
                if (citationsReadyData.search_enrichment_debug) {
                  updatedMessage.metadata!.searchEnrichmentDebug = citationsReadyData.search_enrichment_debug;
                }
                
                // Keep a reference to the updated message for subsequent callbacks
                responseMessage = updatedMessage;
                
                // Update the message in the state
                setMessages(prev => {
                  const updated = prev.map(msg => 
                    msg.id === updatedMessage.id ? updatedMessage : msg
                  );
                  // Pass activeConversationId explicitly for reliability
                  saveCurrentConversation(updated, activeConversationId || undefined);
                  return updated;
                });
                
                // Check if this is the final callback (search enrichment field exists, even if empty)
                // The backend sends search_enrichment as an array when completed, even if empty
                const hasSearchEnrichmentField = 'search_enrichment' in citationsReadyData;
                const hasCitations = !!citationsReadyData.citation_descriptions;
                
                if (hasSearchEnrichmentField) {
                  // Final callback with search enrichment field - clear loading state
                  console.log('Search enrichment complete, clearing loading state');
                  if (activeConversationId) {
                    clearConversationLoadingState(activeConversationId);
                    markProgressReceived(); // Ensure timeout is cleared
                  }
                } else if (hasCitations) {
                  // Citations ready but search enrichment not yet complete - update progress message
                  console.log('Citations ready, waiting for search enrichment');
                  updateConversationLoadingState(activeConversationId, {
                    currentStage: 'search_enrichment',
                    stageMessage: 'Enriching with web research...',
                    progress: 97,
                  });
                }
              }
            },
            abortController.signal
          );
      
      // Clear job tracking for this conversation - only for clarification responses
      // For two-phase responses, this is handled in the onCitationsReady callback
      if (activeConversationId && isClarificationResponse) {
        clearConversationLoadingState(activeConversationId);
        markProgressReceived(); // Ensure timeout is cleared
      }
      
      // Check if clarification is required
      if (response.requires_clarification && response.clarification_questions) {
        // Set pending clarification state
        setPendingClarification({
          originalQuery: content,
          questions: response.clarification_questions,
        });
        
        // Create a special assistant message for clarification
        const clarificationMessage: Message = {
          id: uuidv4(),
          role: 'assistant',
          content: `I need some clarification to better answer your question:\n\n${response.clarification_questions.map((q: string, i: number) => `${i + 1}. ${q}`).join('\n')}`,
          timestamp: new Date(),
          metadata: {
            requiresClarification: true,
            clarificationQuestions: response.clarification_questions,
            model: selectedModel,
          },
        };
        
        const finalMessages = [...updatedMessages, clarificationMessage];
        setMessages(finalMessages);
        // Pass activeConversationId explicitly for reliability
        saveCurrentConversation(finalMessages, activeConversationId);
        
        // Clear stage state - use activeConversationId for consistency
        if (activeConversationId) {
          clearConversationLoadingState(activeConversationId);
        }
        return;
      }
      
      // Clear any pending clarification (normal response received)
      if (pendingClarification) {
        setPendingClarification(null);
      }
      
      // Create the assistant's response message only if not already created in two-phase response
      let finalAssistantMessage: Message;
      
      if (responseMessage) {
        // Two-phase response: update the existing message with final data
        finalAssistantMessage = responseMessage;
        
        // Update citation status
        finalAssistantMessage.metadata!.citationStatus = 'completed';
        
        // Update content if the final response has better citation integration
        if (response.cited_response && response.cited_response !== finalAssistantMessage.content) {
          finalAssistantMessage.content = response.cited_response;
        }
        
        // Update citations with final data
        if (response.citation_descriptions) {
          finalAssistantMessage.metadata!.citationDescriptions = response.citation_descriptions;
        }
        if (response.citation_metadata) {
          finalAssistantMessage.metadata!.citationMetadata = response.citation_metadata;
        }
        
        // Update search enrichment with final data
        if (response.search_enrichment) {
          finalAssistantMessage.metadata!.searchEnrichment = response.search_enrichment;
        }
        // Update search enrichment debug info (dev mode only)
        if (response.search_enrichment_debug) {
          finalAssistantMessage.metadata!.searchEnrichmentDebug = response.search_enrichment_debug;
        }
        
        // Update the message in the state
        setMessages(prev => {
          const updated = prev.map(msg => 
            msg.id === finalAssistantMessage.id ? finalAssistantMessage : msg
          );
          // Pass activeConversationId explicitly for reliability
          saveCurrentConversation(updated, activeConversationId || undefined);
          return updated;
        });
        
      } else {
        // Regular response (clarification or fallback): create new message
        finalAssistantMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: response.cited_response || response.response || "Unable to process your request.",
          timestamp: new Date(),
          metadata: {
            citationStatus: 'completed',
            jobId: jobId || undefined,
            model: selectedModel,
          },
        };
        
        // Ensure metadata exists
        if (!finalAssistantMessage.metadata) {
          finalAssistantMessage.metadata = {};
        }
        
        // Add citation data if available
        if (response.citation_descriptions) {
          finalAssistantMessage.metadata.citationDescriptions = response.citation_descriptions;
        }
        if (response.citation_metadata) {
          finalAssistantMessage.metadata.citationMetadata = response.citation_metadata;
        }
        
        // Add SQL metadata if available
        if (response.sqlQuery) {
          finalAssistantMessage.metadata.sqlQuery = response.sqlQuery;
        }
        
        // Add table data if available
        if (response.tableData) {
          finalAssistantMessage.metadata.tableData = response.tableData;
        }
        
        // Add visualization if available
        if (response.visualization) {
          finalAssistantMessage.metadata.visualization = response.visualization;
        }
        
        // Add search enrichment if available
        if (response.search_enrichment) {
          finalAssistantMessage.metadata.searchEnrichment = response.search_enrichment;
        }
        // Add search enrichment debug info (dev mode only)
        if (response.search_enrichment_debug) {
          finalAssistantMessage.metadata.searchEnrichmentDebug = response.search_enrichment_debug;
        }
        
        // Add follow-up suggestions if available
        if (response.followUpSuggestions && response.followUpSuggestions.length) {
          finalAssistantMessage.followUps = response.followUpSuggestions;
        }
        
        // Add the assistant message
        const finalMessages = [...updatedMessages, finalAssistantMessage];
        setMessages(finalMessages);
        
        // Log assistant response
        if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
          const responseLength = typeof finalAssistantMessage.content === 'string' 
            ? finalAssistantMessage.content.length 
            : JSON.stringify(finalAssistantMessage.content).length;
          const responseTime = Date.now() - userMessage.timestamp.getTime();
          
          logger.logAssistantResponse(
            userEmail, 
            activeConversationId, 
            finalAssistantMessage.id, 
            responseLength, 
            responseTime, 
            false, 
            sessionId, 
            jobId || undefined
          );
        }
        
        // Save the conversation to localStorage with the final messages
        // Pass activeConversationId explicitly for reliability
        saveCurrentConversation(finalMessages, activeConversationId);
        
        // Trigger notification for query completion (single-phase response) if not muted
        try {
          const muted = localStorage.getItem(NOTIFICATIONS_MUTED_KEY) === 'true';
          if (!muted) {
            notifyQueryComplete(content, finalAssistantMessage.content).catch(err => 
              console.warn('Failed to send notification:', err)
            );
          }
        } catch (e) {
          // ignore localStorage errors
        }
        
        // Clear stage state for regular responses (non-two-phase)
        if (activeConversationId) {
          clearConversationLoadingState(activeConversationId);
          markProgressReceived(); // Ensure timeout is cleared
        }
      }
      
      // Note: For two-phase responses, loading state is cleared in onCitationsReady callback
      
    } catch (error: any) {
      // Handle abort errors gracefully (don't show as API errors)
      if (error.name === 'AbortError') {
        console.log('Request was aborted by user');
        if (currentConversationId) {
          clearConversationLoadingState(currentConversationId);
          // Note: markProgressReceived is not available in error scope, timeout will naturally expire
        }
        return; // Don't set API error for user-initiated cancellations
      }
      
      console.error('Error sending message:', error);
      setApiError(error instanceof Error ? error : new Error('Failed to send message'));
      
      // Log user error
      if (FEATURE_FLAGS.ENABLE_CONVERSATION_LOGGING && userEmail) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
        logger.logUserError(userEmail, errorMessage, { 
          conversationId: currentConversationId,
          userMessage: content 
        }, currentConversationId || undefined, sessionId);
      }
      
      // Trigger error notification
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      try {
        const muted = localStorage.getItem(NOTIFICATIONS_MUTED_KEY) === 'true';
        if (!muted) {
          notifyError(errorMessage).catch(err => 
            console.warn('Failed to send error notification:', err)
          );
        }
      } catch (e) {
        // ignore localStorage errors
      }
      
      if (currentConversationId) {
        clearConversationLoadingState(currentConversationId);
        // Note: markProgressReceived is not available in error scope, timeout will naturally expire
      }
    }
  }, [messages, currentConversationId, startNewConversation, saveCurrentConversation, pendingClarification, setPendingClarification, updateConversationLoadingState, clearConversationLoadingState, getConversationLoadingState, userEmail, sessionId, isInitialized, searchEnrichmentEnabled]);

  // Clear messages (start new conversation)
  const clearMessages = useCallback(() => {
    startNewConversation();
  }, [startNewConversation]);

  // Enable mock mode
  const enableMockMode = useCallback(() => {
    localStorage.setItem('useMockApi', 'true');
    window.location.reload();
  }, []);

  // Retry connection
  const retryConnection = useCallback(() => {
    setApiError(null);
    chatApi.checkHealth()
      .then(isHealthy => {
        if (isHealthy) {
          console.log("Backend connection restored");
        } else {
          setApiError(new Error("Backend service is not healthy"));
        }
      })
      .catch(error => {
        setApiError(error);
      });
  }, []);

  return (
    <ChatContext.Provider value={{ 
      messages, 
      isLoading: conversationIsLoading, 
      apiError,
      conversations,
      currentConversationId,
      currentStage,
      stageMessage,
      progress,
      pendingClarification,
      storageWarning,
      sendMessage, 
      stopMessage,
      clearMessages,
      enableMockMode,
      retryConnection,
      startNewConversation,
      loadConversation,
      deleteConversation,
      saveCurrentConversation,
      clearStorageWarning,
      getStorageInfo,
      forceCleanupOldConversations,
      exportConversationsAsJSON,
      importConversationsFromJSON,
      isSidebarCollapsed,
      setSidebarCollapsed,
      searchEnrichmentEnabled,
      setSearchEnrichmentEnabled,
      isConversationInProgress,
      updateMessageMetadata
    }}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook for using the chat context
export const useChat = () => useContext(ChatContext);
